/**
 * Main Dashboard Page
 * 
 * Displays key metrics and recent activity across the rewards system.
 * Uses useDashboardData hook for data aggregation.
 * Supports date range and partner filtering for stats and charts.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody, StatCard } from '@/components/common/Card';
import { Table, type Column } from '@/components/common/Table';
import { Select } from '@/components/common/FormField';
import { ActivityTypeBadge, FulfillmentStatusBadge, RewardCategoryBadge } from '@/components/common/Badge';
import { DateRangeSelector, getDateRangeFromPreset, type DateRange } from '@/components/common/DateRangeSelector';
import { useAuth } from '@/app/contexts/AuthContext';
import { useDashboardData } from '@/app/hooks/useDashboardData';
import {
  fetchIncommCardsPartnerBreakdown,
  fetchIncommCardsSummary,
} from '@/app/services/incommCardService';
import { partnerService } from '@/app/services/partnerService';
import { hasPermission } from '@/app/utils/permissions';
import type { ActivityItem } from '@/app/types/dashboard';
import {
  buildIncommListSearchString,
  countUnsetCardholdersForPartner,
  countUnsetCardholdersFromSummary,
  totalCardholdersForPartner,
} from '@/pages/IncommCards/incommListNavigation';
import styles from './Dashboard.module.scss';

/**
 * Format a timestamp to relative time (e.g., "2 minutes ago")
 */
function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  
  return then.toLocaleDateString();
}

/**
 * Get display label for stats based on date range
 */
function getStatsLabel(preset: string): string {
  switch (preset) {
    case '5m': return '5 min';
    case '15m': return '15 min';
    case '30m': return '30 min';
    case '1h': return '1 hour';
    case '6h': return '6 hours';
    case '24h': return '24h';
    case '7d': return '7 days';
    case '30d': return '30 days';
    case '90d': return '90 days';
    case 'all': return 'All time';
    case 'custom': return 'Selected';
    default: return '';
  }
}

/**
 * Simple bar visualization using CSS
 */
const PointsBar: React.FC<{ earned: number; redeemed: number; maxValue: number }> = ({ 
  earned, 
  redeemed, 
  maxValue 
}) => {
  const earnedWidth = maxValue > 0 ? (earned / maxValue) * 100 : 0;
  const redeemedWidth = maxValue > 0 ? (redeemed / maxValue) * 100 : 0;
  
  return (
    <div className={styles.barContainer}>
      <div className={styles.barEarned} style={{ width: `${earnedWidth}%` }} />
      <div className={styles.barRedeemed} style={{ width: `${redeemedWidth}%` }} />
    </div>
  );
};

// Default to Last 7 days so mock data shows
const DEFAULT_DATE_RANGE: DateRange = {
  preset: '7d',
  ...getDateRangeFromPreset('7d'),
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const canIncommIntegrations = hasPermission(userProfile, 'integrations:read');
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  
  // Partner filter state (same pattern as OfferFilters)
  const [partnerOptions, setPartnerOptions] = useState<{ value: string; label: string }[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);

  // Fetch partners for dropdown
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setPartnersLoading(true);
        const response = await partnerService.queryPartners({
          is_active: true,
          limit: 1000,
          offset: 0,
        });
        
        // Sort by DBA alphabetically (same as OfferFilters)
        const sortedPartners = [...response.partners].sort((a, b) => {
          const dbaA = (a.doing_business_as || '').toLowerCase();
          const dbaB = (b.doing_business_as || '').toLowerCase();
          return dbaA.localeCompare(dbaB);
        });
        
        const options = [
          { value: '', label: 'All Partners' },
          ...sortedPartners.map((p) => ({
            value: p.partner_product_id.toString(),
            label: `${p.doing_business_as} (${p.partner_product_id})`,
          })),
        ];
        setPartnerOptions(options);
      } catch (error) {
        console.error('Failed to fetch partners:', error);
        setPartnerOptions([{ value: '', label: 'All Partners' }]);
      } finally {
        setPartnersLoading(false);
      }
    };
    fetchPartners();
  }, []);

  const { data, isLoading, error, refresh } = useDashboardData(dateRange, selectedPartnerId);

  const [incommUnsetCount, setIncommUnsetCount] = useState<number | null>(null);
  const [incommTotalCardholders, setIncommTotalCardholders] = useState<number | null>(null);
  const [incommLoading, setIncommLoading] = useState(false);
  const [incommLoadFailed, setIncommLoadFailed] = useState(false);

  useEffect(() => {
    if (!canIncommIntegrations) {
      setIncommUnsetCount(null);
      setIncommTotalCardholders(null);
      setIncommLoadFailed(false);
      return;
    }
    let cancelled = false;
    const loadIncomm = async (): Promise<void> => {
      setIncommLoading(true);
      setIncommLoadFailed(false);
      try {
        if (selectedPartnerId == null) {
          const summary = await fetchIncommCardsSummary();
          if (!cancelled) {
            setIncommUnsetCount(countUnsetCardholdersFromSummary(summary));
            setIncommTotalCardholders(summary.total_cardholders);
          }
        } else {
          const breakdown = await fetchIncommCardsPartnerBreakdown();
          if (!cancelled) {
            setIncommUnsetCount(countUnsetCardholdersForPartner(breakdown, selectedPartnerId));
            setIncommTotalCardholders(totalCardholdersForPartner(breakdown, selectedPartnerId));
          }
        }
      } catch {
        if (!cancelled) {
          setIncommUnsetCount(null);
          setIncommTotalCardholders(null);
          setIncommLoadFailed(true);
        }
      } finally {
        if (!cancelled) {
          setIncommLoading(false);
        }
      }
    };
    void loadIncomm();
    return () => {
      cancelled = true;
    };
  }, [canIncommIntegrations, selectedPartnerId]);

  const statsLabel = getStatsLabel(dateRange.preset);

  // Handle click on Failed Fulfillments card - navigate to filtered fulfillments list
  const handleFailedFulfillmentsClick = useCallback(() => {
    navigate('/fulfillments', { state: { status: 'FAILED' } });
  }, [navigate]);

  const handleIncommNotSentClick = useCallback(() => {
    navigate(`/incomm-cards/list${buildIncommListSearchString({ cardStatusUnset: true, partnerProductId: selectedPartnerId })}`);
  }, [navigate, selectedPartnerId]);

  const handleIncommCardholdersClick = useCallback(() => {
    if (selectedPartnerId != null) {
      navigate(`/incomm-cards/list${buildIncommListSearchString({ partnerProductId: selectedPartnerId })}`);
    } else {
      navigate('/incomm-cards');
    }
  }, [navigate, selectedPartnerId]);

  // Build stats array from data
  const stats = data ? [
    { 
      label: `Points Earned (${statsLabel})`, 
      value: data.stats.pointsEarnedToday, 
      onClick: undefined,
    },
    { 
      label: `Points Redeemed (${statsLabel})`, 
      value: data.stats.pointsRedeemedToday, 
      onClick: undefined,
    },
    { 
      label: 'Failed Fulfillments', 
      value: data.stats.failedFulfillmentsCount, 
      // Only show action hint when there are failures to view
      change: data.stats.failedFulfillmentsCount > 0 
        ? { value: 'Click to view', trend: 'down' as const }
        : undefined,
      onClick: data.stats.failedFulfillmentsCount > 0 ? handleFailedFulfillmentsClick : undefined,
    },
  ] : [];

  // Handle row click to navigate to detail page
  const handleRowClick = useCallback((item: ActivityItem) => {
    navigate(item.linkTo);
  }, [navigate]);

  // Calculate max value for chart scaling
  const maxPointsValue = data?.pointsActivity.reduce((max, day) => {
    return Math.max(max, day.earned, day.redeemed);
  }, 0) || 0;

  // Table columns for activity feed
  const columns: Column<ActivityItem>[] = [
    {
      key: 'type',
      header: 'Type',
      width: '120px',
      render: (item) => <ActivityTypeBadge type={item.type} />,
    },
    {
      key: 'description',
      header: 'Description',
      render: (item) => (
        <div className={styles.activityDescription}>
          <span className={styles.activityTitle}>{item.title}</span>
          <span className={styles.activitySubtitle}>{item.description}</span>
        </div>
      ),
    },
    {
      key: 'points',
      header: 'Points',
      width: '100px',
      align: 'right',
      hideOnMobile: true,
      render: (item) => {
        if (item.points == null) return '—';
        const isPositive = item.points >= 0;
        return (
          <span className={`${styles.points} ${isPositive ? styles.positive : styles.negative}`}>
            {isPositive ? '+' : ''}{item.points.toLocaleString()}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      hideOnMobile: true,
      render: (item) => {
        if (item.type === 'fulfillment' && item.status) {
          return <FulfillmentStatusBadge status={item.status} />;
        }
        return null;
      },
    },
    {
      key: 'timestamp',
      header: 'Time',
      width: '140px',
      render: (item) => (
        <span className={styles.timestamp}>{formatRelativeTime(item.timestamp)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (item) => (
        <button 
          className={styles.btnView}
          onClick={(e) => {
            e.stopPropagation();
            navigate(item.linkTo);
          }}
        >
          View
        </button>
      ),
    },
  ];

  // Custom mobile card renderer
  const renderMobileCard = (item: ActivityItem) => (
    <div className={styles.mobileCard}>
      <div className={styles.mobileCardHeader}>
        <ActivityTypeBadge type={item.type} />
        {item.type === 'fulfillment' && item.status && (
          <FulfillmentStatusBadge status={item.status} />
        )}
      </div>
      <div className={styles.mobileCardBody}>
        <span className={styles.activityTitle}>{item.title}</span>
        <span className={styles.activitySubtitle}>{item.description}</span>
      </div>
      <div className={styles.mobileCardFooter}>
        {item.points != null && (
          <span className={`${styles.points} ${item.points >= 0 ? styles.positive : styles.negative}`}>
            {item.points >= 0 ? '+' : ''}{item.points.toLocaleString()} pts
          </span>
        )}
        <span className={styles.timestamp}>{formatRelativeTime(item.timestamp)}</span>
      </div>
    </div>
  );

  return (
    <div className={styles.dashboard}>
      {/* Dashboard Header with Date Range and Partner Selectors */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerLeft}>
          <h2 className={styles.pageTitle}>Overview</h2>
          <span className={styles.pageSubtitle}>Key metrics and recent activity</span>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.partnerFilter}>
            <Select
              value={selectedPartnerId?.toString() || ''}
              onChange={(val) => setSelectedPartnerId(val ? parseInt(val, 10) : null)}
              options={partnerOptions}
              disabled={partnersLoading}
            />
          </div>
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
        </div>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        {isLoading ? (
          <>
            <div className={styles.statSkeleton} />
            <div className={styles.statSkeleton} />
            <div className={styles.statSkeleton} />
          </>
        ) : (
          stats.map((stat) => (
            <StatCard
              key={stat.label}
              label={stat.label}
              value={stat.value}
              change={stat.change}
              onClick={stat.onClick}
            />
          ))
        )}
        {canIncommIntegrations && (
          <>
            {incommLoading ? (
              <>
                <div className={styles.statSkeleton} />
                <div className={styles.statSkeleton} />
              </>
            ) : (
              <>
                <StatCard
                  label={
                    selectedPartnerId != null
                      ? 'Not sent to InComm yet (partner)'
                      : 'Not sent to InComm yet'
                  }
                  value={incommLoadFailed ? '—' : (incommUnsetCount ?? 0)}
                  change={
                    incommLoadFailed
                      ? undefined
                      : { value: 'Open filtered list →', trend: 'down' }
                  }
                  onClick={handleIncommNotSentClick}
                />
                <StatCard
                  label={
                    selectedPartnerId != null
                      ? 'InComm cardholders (partner)'
                      : 'InComm cardholders'
                  }
                  value={incommLoadFailed ? '—' : (incommTotalCardholders ?? 0)}
                  change={
                    incommLoadFailed
                      ? undefined
                      : {
                          value: selectedPartnerId != null ? 'Partner list →' : 'Summary →',
                          trend: 'up',
                        }
                  }
                  onClick={handleIncommCardholdersClick}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Charts Grid */}
      <div className={styles.chartsGrid}>
        {/* Points Activity Chart */}
        <Card>
          <CardHeader 
            title="Points Activity" 
            subtitle={dateRange.preset === 'all' ? 'All time (last 14 days shown)' : `Last ${statsLabel}`} 
          />
          <CardBody>
            {isLoading ? (
              <div className={styles.chartSkeleton} />
            ) : data?.pointsActivity && data.pointsActivity.length > 0 ? (
              <div className={styles.pointsChart}>
                <div className={styles.chartLegend}>
                  <span className={styles.legendEarned}>● Earned</span>
                  <span className={styles.legendRedeemed}>● Redeemed</span>
                </div>
                <div className={styles.chartRows}>
                  {data.pointsActivity.map((day) => (
                    <div key={day.date} className={styles.chartRow}>
                      <span className={styles.chartDayLabel}>{day.dayLabel}</span>
                      <PointsBar 
                        earned={day.earned} 
                        redeemed={day.redeemed} 
                        maxValue={maxPointsValue} 
                      />
                      <div className={styles.chartValues}>
                        <span className={styles.chartEarned}>+{day.earned.toLocaleString()}</span>
                        <span className={styles.chartRedeemed}>-{day.redeemed.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={styles.chartTotals}>
                  <span>
                    Total Earned: <strong className={styles.positive}>
                      +{data.pointsActivity.reduce((sum, d) => sum + d.earned, 0).toLocaleString()}
                    </strong>
                  </span>
                  <span>
                    Total Redeemed: <strong className={styles.negative}>
                      -{data.pointsActivity.reduce((sum, d) => sum + d.redeemed, 0).toLocaleString()}
                    </strong>
                  </span>
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>No activity data for selected period</div>
            )}
          </CardBody>
        </Card>

        {/* Top Rewards */}
        <Card>
          <CardHeader 
            title="Top Rewards" 
            subtitle={`Most redeemed (${statsLabel})`}
            action={
              <button 
                className={styles.btnLink}
                onClick={() => navigate('/rewards')}
              >
                View All →
              </button>
            }
          />
          <CardBody>
            {isLoading ? (
              <div className={styles.chartSkeleton} />
            ) : data?.topRewards && data.topRewards.length > 0 ? (
              <div className={styles.topRewardsList}>
                {data.topRewards.map((reward, index) => (
                  <div 
                    key={reward.rewardId} 
                    className={styles.topRewardItem}
                    onClick={() => navigate(`/rewards/${reward.rewardId}`)}
                  >
                    <span className={styles.rewardRank}>#{index + 1}</span>
                    <div className={styles.rewardInfo}>
                      <span className={styles.rewardName}>{reward.rewardName}</span>
                      <RewardCategoryBadge category={reward.category} />
                    </div>
                    <div className={styles.rewardStats}>
                      <span className={styles.rewardCount}>{reward.redemptionCount} redemptions</span>
                      <span className={styles.rewardPoints}>{reward.totalPoints.toLocaleString()} pts</span>
                      <span className={styles.rewardDollarValue}>${reward.totalDollarValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <div className={styles.statusBreakdown}>
                        {reward.statusBreakdown.completed > 0 && (
                          <span className={styles.statusBadge} data-status="completed" title={`${reward.statusBreakdown.completed} completed fulfillments`}>
                            Completed: {reward.statusBreakdown.completed}
                          </span>
                        )}
                        {reward.statusBreakdown.pending > 0 && (
                          <span className={styles.statusBadge} data-status="pending" title={`${reward.statusBreakdown.pending} pending fulfillments`}>
                            Pending: {reward.statusBreakdown.pending}
                          </span>
                        )}
                        {reward.statusBreakdown.failed > 0 && (
                          <span className={styles.statusBadge} data-status="failed" title={`${reward.statusBreakdown.failed} failed fulfillments`}>
                            Failed: {reward.statusBreakdown.failed}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>No redemptions for selected period</div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Recent Activity Table - Always shows most recent, not filtered */}
      <Card>
        <CardHeader 
          title="Recent Activity" 
          subtitle="Latest transactions and fulfillments (not filtered by date)"
          action={
            <button 
              className={styles.btnRefresh}
              onClick={refresh}
              disabled={isLoading}
            >
              ↻ Refresh
            </button>
          }
        />
        <CardBody>
          {error && (
            <div className={styles.errorBanner}>
              <span>⚠️ {error}</span>
              <button onClick={refresh}>Retry</button>
            </div>
          )}
          
          <Table<ActivityItem>
            data={data?.activity || []}
            columns={columns}
            keyExtractor={(item) => `${item.type}-${item.id}`}
            loading={isLoading}
            onRowClick={handleRowClick}
            emptyMessage="No recent activity"
            renderMobileCard={renderMobileCard}
          />
        </CardBody>
      </Card>
    </div>
  );
};

export default Dashboard;
